var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, Typography, } from '@mui/material';
import { times } from 'lodash';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MultiAutocomplete, NumberInput, UnControlledFormField } from '../../common';
import { useFormlessContext } from '../../hooks';
import { useDetailTimeline, useEffectiveLocationIndexMap, useEffectiveQueueLocations, useLocationOptions, usePlanType, } from '../selectors';
import { useWizardSimpleContext } from '../useWizardSimpleContext';
import { StyledCell, StyledHeaderRow, StyledPeriodList, StyledPeriodRow } from './styles';
export var PeriodSelector = function (_a) {
    var periodLabels = _a.periodLabels, selectedWeeks = _a.selectedWeeks, periodLength = _a.periodLength, showDateColumn = _a.showDateColumn, planType = _a.planType, onSelectAll = _a.onSelectAll, onWeekToggle = _a.onWeekToggle;
    var t = useTranslation('translation').t;
    var isAllSelected = selectedWeeks.length === periodLength && !!periodLength;
    return (_jsxs(Stack, { children: [_jsxs(StyledHeaderRow, __assign({ direction: "row", alignItems: "center", justifyContent: "space-between" }, { children: [_jsxs(Typography, __assign({ variant: "body2", fontWeight: "bold" }, { children: [t('quickInput.choosePeriods', { planType: planType }), ":"] })), _jsx(FormControlLabel, { sx: { m: 0 }, labelPlacement: "start", label: _jsx(Typography, __assign({ variant: "body2" }, { children: t('quickInput.selectAll') })), control: _jsx(Checkbox, { size: "small", checked: isAllSelected, indeterminate: !!selectedWeeks.length && selectedWeeks.length < periodLength, onChange: function (_event, checked) { return onSelectAll(checked); } }) })] })), _jsx(StyledPeriodList, { children: periodLabels.map(function (p, idx) { return (_jsxs(StyledPeriodRow, __assign({ component: "label" }, { children: [_jsx(StyledCell, __assign({ sx: { flex: 1 } }, { children: _jsx(Typography, __assign({ variant: "body2", fontWeight: showDateColumn ? 'bold' : 'regular' }, { children: p.label })) })), showDateColumn && (_jsx(StyledCell, __assign({ sx: { flex: 1, justifyContent: 'center' } }, { children: _jsx(Typography, __assign({ variant: "body2" }, { children: p.date })) }))), _jsx(StyledCell, __assign({ sx: { width: '4.5rem', justifyContent: 'center', padding: '2px' } }, { children: _jsx(Checkbox, { size: "small", checked: selectedWeeks.includes(idx), onChange: function () { return onWeekToggle(idx); } }) }))] }), "period-".concat(idx, "-").concat(p.label))); }) })] }));
};
export var QuickInputDialog = function (_a) {
    var _b, _c;
    var open = _a.open, onClose = _a.onClose, name = _a.name, _d = _a.min, min = _d === void 0 ? 1 : _d, _e = _a.max, max = _e === void 0 ? 99999 : _e, decimal = _a.decimal, suffix = _a.suffix, onApply = _a.onApply;
    var t = useTranslation(['translation', 'wizard']).t;
    var plan = ((_b = useWizardSimpleContext()) !== null && _b !== void 0 ? _b : {}).plan;
    var planType = usePlanType();
    var _f = (plan !== null && plan !== void 0 ? plan : {}).periodLength, periodLength = _f === void 0 ? 0 : _f;
    var timelines = useDetailTimeline();
    var queueLocations = useEffectiveQueueLocations();
    var setValue = ((_c = useFormlessContext()) !== null && _c !== void 0 ? _c : {}).setValue;
    var clearErrors = useFormContext().clearErrors;
    var _g = useState(null), valueLocal = _g[0], setValueLocal = _g[1];
    var _h = useState([]), selectedLocations = _h[0], setSelectedLocations = _h[1];
    var _j = useState([]), selectedWeeks = _j[0], setSelectedWeeks = _j[1];
    var locationOptions = useLocationOptions();
    var indexMap = useEffectiveLocationIndexMap();
    var hasLocations = !!queueLocations.length;
    var showDateColumn = false;
    var periodLabels = useMemo(function () {
        var _a, _b;
        if (!(timelines === null || timelines === void 0 ? void 0 : timelines.length))
            return [];
        var names = (_b = (_a = timelines[0]) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : [];
        return times(periodLength, function (i) { return ({ label: names[i], date: '' }); });
    }, [timelines, periodLength]);
    var resetState = function () {
        setValueLocal(null);
        setSelectedLocations([]);
        setSelectedWeeks([]);
    };
    var handleClose = function () {
        onClose();
        resetState();
    };
    var handleSelectAll = function (checked) {
        setSelectedWeeks(checked ? times(periodLength) : []);
    };
    var handleWeekToggle = function (idx) {
        setSelectedWeeks(function (prev) { return (prev.includes(idx) ? prev.filter(function (w) { return w !== idx; }) : __spreadArray(__spreadArray([], prev, true), [idx], false)); });
    };
    var handleDone = function () {
        if (valueLocal === null || (!setValue && !onApply))
            return;
        if (onApply) {
            onApply(valueLocal, selectedLocations, selectedWeeks);
        }
        else if (hasLocations) {
            selectedWeeks.forEach(function (periodIdx) {
                queueLocations.forEach(function (loc) {
                    if (selectedLocations.includes(loc.id)) {
                        var locIdx = indexMap.get(loc.id);
                        clearErrors('queueLocations');
                        setValue("queueLocations.".concat(locIdx, ".volumes.").concat(periodIdx, ".").concat(name), valueLocal, {
                            shouldDirty: true,
                        });
                    }
                });
            });
        }
        else {
            selectedWeeks.forEach(function (periodIdx) {
                clearErrors('volumes');
                setValue("volumes.".concat(periodIdx, ".").concat(name), valueLocal, {
                    shouldDirty: true,
                });
            });
        }
        handleClose();
    };
    return (_jsxs(Dialog, __assign({ open: open, onClose: handleClose, maxWidth: "xs" }, { children: [_jsx(DialogTitle, __assign({ component: "div" }, { children: _jsx(Typography, __assign({ variant: "h3" }, { children: t('quickInput.title') })) })), _jsx(DialogContent, { children: _jsxs(Stack, __assign({ spacing: 4 }, { children: [_jsxs(Stack, __assign({ direction: "row", spacing: 4 }, { children: [_jsx(UnControlledFormField, __assign({ label: t('quickInput.enterValue') }, { children: _jsx(NumberInput, { min: min, max: max, value: valueLocal, onChange: function (v) { return setValueLocal(v); }, suffix: suffix, size: "small", decimal: decimal }) })), hasLocations && (_jsx(UnControlledFormField, __assign({ label: t('quickInput.chooseLocation'), sx: { flex: 1 } }, { children: _jsx(MultiAutocomplete, { options: locationOptions, value: selectedLocations, onChange: function (val) { return setSelectedLocations(val); }, sx: { width: '100%' } }) })))] })), _jsx(PeriodSelector, { periodLabels: periodLabels, selectedWeeks: selectedWeeks, periodLength: periodLength, showDateColumn: showDateColumn, planType: planType, onSelectAll: handleSelectAll, onWeekToggle: handleWeekToggle })] })) }), _jsx(DialogActions, __assign({ sx: { justifyContent: 'flex-end' } }, { children: _jsx(Button, __assign({ variant: "contained", onClick: handleDone, disabled: valueLocal === null || !selectedWeeks.length || (hasLocations && !selectedLocations.length) }, { children: t('action.done') })) }))] })));
};
